import { Box } from '@mui/material';
import { Component } from 'react';
import type { ErrorInfo } from 'react';

import { ErrorState } from '../ErrorState';
import { contentWidthSx, pageGutterSx } from '../../theme';
import type { ErrorBoundaryProps, ErrorBoundaryState } from './types';

/**
 * The last line before a blank white page.
 *
 * **A render-time throw unmounts the whole tree**, so without this the app goes
 * white with no chrome, no message and no way back — and nothing in the console
 * a person would look at. This catches it and draws something with a route home.
 *
 * **A class, because there is no hook for this.** `componentDidCatch` and
 * `getDerivedStateFromError` have no function equivalent; React Router's
 * `errorElement` covers loader and action throws, which this app has none of
 * because RTK Query owns the fetching. This is the only mechanism that catches a
 * throw from a component's own render.
 *
 * **It does not overlap `ContentState`, and the two are different problems.** A
 * failed request is expected, recoverable and has a query to retry — that is
 * `ContentState` calling `ErrorState` with `onRetry`. A crash is a bug: the
 * component state is unusable, there is nothing to refetch, and the only honest
 * offer is to start again. So this reuses the same `ErrorState` for the visual —
 * the design Figma draws — with a link home instead of Try again.
 *
 * **What it deliberately does not do is catch async failures.** An error thrown
 * in an event handler or a promise never passes through render, so it never
 * reaches here. That is a React limitation rather than a decision, and it is why
 * this is a floor rather than a safety net.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasCrashed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasCrashed: true };
  }

  /**
   * **The console is the only place the cause goes.** There is no error
   * reporting service wired up, and putting the message on screen would show a
   * stack trace to someone who cannot act on it — the same reasoning that keeps
   * a 5xx's real message out of an API response.
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('render crash caught by ErrorBoundary', {
      errorName: error.name,
      errorMessage: error.message,
      componentStack: info.componentStack,
    });
  }

  override render() {
    if (!this.state.hasCrashed) {
      return this.props.children;
    }

    return (
      <Box sx={pageGutterSx}>
        <Box sx={contentWidthSx}>
          <ErrorState
            title="Something went wrong"
            /**
             * **No Try again, and a full reload rather than a router
             * navigation.** The tree that threw is still mounted above this, so
             * navigating within the app would re-render the same broken state.
             * `href` rather than a `Link` is what makes it a fresh document.
             */
            message="This page hit a problem it could not recover from. Reloading usually clears it — if it keeps happening, the toolkit team would want to know."
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', paddingBottom: 12 }}>
            {/* A real anchor: a full reload is the point, not a route change. */}
            <a href="/">Back to the toolkit</a>
          </Box>
        </Box>
      </Box>
    );
  }
}
